import { Injectable } from '@angular/core';
import { Namespace } from 'enums-lib';
import { IDataSourceApiService, IDocumentDestinationConfig, IDocumentDestinationOptions, Section } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { ISalesforceObjectMapping } from '../../components/adv2-config/adv2-config';
import { IDocumentDestinationService } from '../../model/interfaces/idocumentdestinationservice';
import { IDestinationsForOutputFileType } from '../entity-type/entity-type-service.factory';
import { EntityTypeService, IEntityLabelPair } from '../entity-type/entity-type.service';
import { SalesforceMappingStoreService } from '../salesforce/salesforce-mapping-store.service';

interface IEntityFieldMap {
  entityName: string;
  fields: string[];
}

@Injectable({ providedIn: 'root' })
export class SalesforceDocumentDestinationService implements IDocumentDestinationService {
  private documentDestinationMetaSubject = new BehaviorSubject<IEntityLabelPair[]>(null);
  private readonly namespace: Namespace;
  documentDestinationOptionsAllowed = true;
  fileTypeOptionsAllowed = false;
  documentDestinationMeta$ = this.documentDestinationMetaSubject.asObservable();
  loading$ = new BehaviorSubject<boolean>(true);

  apiService: IDataSourceApiService;

  clientLeadOpportunityEnabled: boolean = undefined;

  private readonly defaultEntityFieldsMap: IEntityFieldMap[] = [];
  private entityFieldsMap: IEntityFieldMap[] = [];

  constructor(
    private identityStorage: IdentityStorage,
    private entityTypeService: EntityTypeService,
    private serviceProviderFactory: SharedServiceProviderServiceFactory,
    private messageNotificationService: MessageNotificationService,
    private salesforceMappingStore: SalesforceMappingStoreService
  ) {
    this.namespace = this.identityStorage.get().namespace;
    this.apiService = this.serviceProviderFactory.getServiceProviderService(this.namespace).getDataSourceApiService();
    this.salesforceMappingStore.getEnabledMappings().subscribe({
      next: (mappings) => {
        this.entityFieldsMap = this.buildEntityFieldsMapFromMappings(mappings);
        this.loadDocumentDestinationMeta();
      },
      error: () => {
        this.entityFieldsMap = [];
        this.loadDocumentDestinationMeta();
      },
    });
  }

  getDocumentDestinationOptions(
    meta: IEntityLabelPair[],
    templateEntityName: string,
    availability: string[] | undefined
  ): Observable<IDocumentDestinationConfig[]> {
    return this.salesforceMappingStore.getEnabledMappings().pipe(
      take(1),
      map((mappings) => this.buildDestinationConfigs(mappings, meta, templateEntityName, availability))
    );
  }

  getMetaLabelFromDestinationPath(meta: IEntityLabelPair[], path: string): string | undefined {
    if (!path) {
      return undefined;
    }
    return meta?.find((m) => m.objectName === path)?.label ?? path;
  }

  getEntityTypeFromDestinationPath(path: string): string {
    return path;
  }

  getDestinationsForOutputFileType(
    meta: IEntityLabelPair[],
    destinationEntityTypes: string[],
    section: Section
  ): IDestinationsForOutputFileType[] {
    return undefined;
  }

  private buildDestinationConfigs(
    mappings: ISalesforceObjectMapping[],
    meta: IEntityLabelPair[],
    templateEntityName: string,
    availability: string[] | undefined
  ): IDocumentDestinationConfig[] {
    const mapping = mappings.find((m) => m.objectName === templateEntityName);
    if (!mapping) {
      return [];
    }

    const getLabel = (objectName: string): string =>
      meta?.find((m) => m.objectName === objectName)?.label
      ?? mappings.find((m) => m.objectName === objectName)?.label
      ?? objectName;

    // Match Bullhorn semantics: emit one IDocumentDestinationConfig per availability value.
    // When availability is omitted, default to the template's own entity type.
    const availabilityList = availability?.length ? availability : [templateEntityName];

    return availabilityList.map((avail) => ({
      entityType: avail,
      label: getLabel(avail),
      options: this.buildOptionsForAvailability(mapping, avail, getLabel),
    }));
  }

  private buildOptionsForAvailability(
    mapping: ISalesforceObjectMapping,
    availability: string,
    getLabel: (objectName: string) => string
  ): IDocumentDestinationOptions[] {
    return [
      {
        path: mapping.objectName,
        default: true,
        availability,
        label: getLabel(mapping.objectName),
        tooltip: mapping.objectName,
      },
      ...(mapping.relatedObjects ?? []).map((r) => ({
        path: r.objectName,
        default: false,
        availability,
        label: getLabel(r.objectName),
        tooltip: `${r.objectName}.${r.relationSegment}`,
      })),
    ];
  }

  private getDocumentDestinationMeta(): Observable<IEntityLabelPair[]> {
    const entityFields = this.getEntityFieldsForDocumentDestination();
    if (!entityFields.length) {
      return of([]);
    }
    return forkJoin(
      entityFields.map((x) => {
        return this.entityTypeService.getEntityFieldsForNamespace(this.namespace, x.entityName, x.fields).pipe(
          catchError((error) => {
            this.messageNotificationService.error('Failed to load meta for document destination.', error);
            return of(error);
          })
        ) as Observable<IEntityLabelPair>;
      })
    );
  }

  private getEntityFieldsForDocumentDestination(): {
    entityName: string;
    fields: string;
  }[] {
    return this.getAvailableEntityFieldsMap().map((x) => ({
      ...x,
      fields: x.fields.join(','),
    }));
  }

  private getAvailableEntityFieldsMap() {
    return this.entityFieldsMap;
  }

  private loadDocumentDestinationMeta(): void {
    this.loading$.next(true);
    this.getDocumentDestinationMeta().subscribe((meta) => {
      this.documentDestinationMetaSubject.next(meta);
      this.loading$.next(false);
    });
  }

  private buildEntityFieldsMapFromMappings(mappings: ISalesforceObjectMapping[]): IEntityFieldMap[] {
    if (!mappings?.length) {
      return [];
    }

    const enabled = mappings.filter((mapping) => mapping.enabled !== false && Boolean(mapping.objectName));
    if (!enabled.length) {
      return [];
    }

    return enabled.map((mapping) => ({
      entityName: mapping.objectName,
      fields: this.buildFieldList(mapping),
    }));
  }

  private buildFieldList(mapping: ISalesforceObjectMapping): string[] {
    const paths = new Set<string>(['Id']);
    (mapping.fields ?? []).forEach((field) => {
      if (field?.path) {
        paths.add(field.path);
      }
    });
    return Array.from(paths);
  }
}
